import React, { useRef, useState } from 'react';
import api from '../utils/api';

const requestFields = [
  ['tenantId', 'Tenant Id', 'number', 'Registered Tenant ID'],
  ['first_name', 'First Name', 'text', 'Registered First Name'],
  ['last_name', 'Last Name', 'text', 'Registered Last Name'],
  ['email', 'Employee Email', 'email', 'Registered Email with Tenant'],
  ['phoneNumber', 'Phone Number', 'tel', 'Contact Number'],
];
const detailFields = [
  ['firstName', 'First name'], ['lastName', 'Last name'],
  ['phoneNumber', 'Phone number', 'tel'], ['email', 'Email', 'email'],
  ['dateOfBirth', 'Date of birth', 'date'],
  ['hno', 'Hno'], ['streetName', 'Street Name'], ['area', 'Area'],
  ['state', 'State'], ['district', 'District'], ['pincode', 'Pincode'],
];

function fieldMaxLength(name) {
  if (name === 'tenantId' || name === 'dateOfBirth') return undefined;
  if (name === 'phoneNumber') return 10;
  if (name === 'pincode') return 6;
  return 50;
}

export default function EmployeeOnboardForm({ onBack }) {
  const [step, setStep] = useState('request');
  const [code, setCode] = useState(Array(6).fill(''));
  const [request, setRequest] = useState({});
  const [details, setDetails] = useState({});
  const [verifiedRequest, setVerifiedRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const inputs = useRef([]);
  const submitting = useRef(false);

  async function submit(event) {
    event.preventDefault();
    if (submitting.current) return;
    setError('');
    const fields = step === 'request' ? requestFields : detailFields;
    const values = step === 'request' ? request : details;
    const payload = {};
    if (step === 'verify') {
      const tenantId = Number(request.tenantId);
      if (!Number.isSafeInteger(tenantId) || tenantId <= 0 || tenantId > 2147483647) {
        setError('Enter a valid Tenant Id to verify your code.');
        return;
      }
      if (!code.every((character) => /^[A-Za-z0-9]$/.test(character))) {
        setError('Enter the complete 6-character verification code.');
        return;
      }
    } else {
      for (const [name, label] of fields) {
        payload[name] = (values[name] || '').trim();
        if (!payload[name]) {
          setError(label + ' is required.');
          return;
        }
        const limit = fieldMaxLength(name);
        if (limit && payload[name].length > limit) {
          setError(label + ' must not exceed ' + limit + ' characters.');
          return;
        }
      }
      if (step === 'request') {
        payload.tenantId = Number(payload.tenantId);
        if (!Number.isSafeInteger(payload.tenantId) || payload.tenantId <= 0 || payload.tenantId > 2147483647) {
          setError('Tenant Id must be a positive whole number.');
          return;
        }
      }
    }
    if (step === 'details' && !verifiedRequest) {
      setError('Verify your code before submitting employee details.');
      return;
    }
    submitting.current = true;
    setLoading(true);
    setMessage('');
    try {
      if (step === 'request') {
        await api.employeeGetCode(payload);
        setVerifiedRequest(null);
        setDetails({});
        setCode(Array(6).fill(''));
        setMessage('Contact your HR for verification code.');
        setStep('verify');
      } else if (step === 'verify') {
        const response = await api.employeeVerifyCode(code.join(''), Number(request.tenantId));
        const verified = response.data;
        if (!verified || Number(verified.tenantId) !== Number(request.tenantId)) {
          throw new Error('Unable to load the verified employee request. Please try again.');
        }
        setVerifiedRequest({ tenantId: Number(verified.tenantId), verificationCode: code.join('') });
        setDetails({
          firstName: verified.first_name || '',
          lastName: verified.last_name || '',
          email: verified.email || '',
          phoneNumber: verified.phoneNumber || '',
        });
        setMessage('Verification code is verified successfully.');
        setStep('details');
      } else {
        await api.employeeRegister({ ...payload, ...verifiedRequest });
        setMessage('Contact your HR to approve your onboarding request.');
        setStep('success');
      }
    } catch (err) {
      setError(err.message || 'Unable to submit your request. Please try again.');
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  }

  function renderFields(fields, values, setValues) {
    return <div className="lp-registration-fields">
      {fields.map(([name, label, type = 'text', placeholder = label]) => {
        const readOnly = step === 'details' && ['firstName', 'lastName', 'email', 'phoneNumber'].includes(name);
        return (
        <div className="lp-field" key={name}>
          <label htmlFor={'employee-' + name}>{label} *</label>
          <input id={'employee-' + name} name={name} type={type} placeholder={placeholder} required maxLength={fieldMaxLength(name)} readOnly={readOnly} disabled={loading}
            {...(type === 'number' ? { min: 1, max: 2147483647, step: 1 } : {})}
            {...(type === 'date' ? { max: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10) } : {})}
            value={values[name] || ''}
            onChange={(event) => setValues((previous) => ({ ...previous, [name]: event.target.value }))} />
        </div>
        );
      })}
    </div>;
  }

  return (
    <div className="lp-card lp-registration lp-employee-onboard">
      <h2 className="lp-title">{step === 'request' ? 'Request Verification Code' :
        step === 'details' ? 'Employee Details' : step === 'success' ? 'Onboarding Request Submitted' : 'Verify Code'}</h2>
      {step !== 'success' && <p className="lp-subtitle">{step === 'verify'
        ? 'Enter your Tenant Id and the verification code provided by HR.'
        : 'All fields marked * are required.'}</p>}
      {error && <div className="lp-error" role="alert">{error}</div>}
      {message && <div className="lp-success" role="status">{message}</div>}
      {step === 'success' ? (
        <button type="button" className="lp-submit" onClick={onBack}>Back to Login</button>
      ) : (
        <form onSubmit={submit} aria-busy={loading}>
          {step === 'verify' && renderFields([['tenantId', 'Tenant Id', 'text', 'Registered Tenant ID']], request, setRequest)}
          {step === 'verify' && (
            <div className="lp-field">
              <label id="employee-code-label">Verify Code *</label>
              <div className="lp-otp-inputs" role="group" aria-labelledby="employee-code-label">
                {code.map((character, index) => (
                  <input key={index} ref={(element) => { inputs.current[index] = element; }}
                    className="lp-otp-box" type="text" maxLength={1} required
                    aria-label={'Verification code character ' + (index + 1)}
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    disabled={loading} value={character}
                    onChange={(event) => {
                      const value = event.target.value.slice(-1);
                      if (value && !/^[A-Za-z0-9]$/.test(value)) return;
                      setCode((previous) => previous.map((item, position) => position === index ? value : item));
                      if (value) inputs.current[index + 1]?.focus();
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Backspace' && !character && index > 0) {
                        event.preventDefault();
                        setCode((previous) => previous.map((item, position) => position === index - 1 ? '' : item));
                        inputs.current[index - 1]?.focus();
                      }
                      if (event.key === 'ArrowLeft') inputs.current[index - 1]?.focus();
                      if (event.key === 'ArrowRight') inputs.current[index + 1]?.focus();
                    }}
                    onPaste={(event) => {
                      event.preventDefault();
                      const pasted = event.clipboardData.getData('text').trim();
                      if (/^[A-Za-z0-9]{6}$/.test(pasted)) {
                        setCode(pasted.split(''));
                        inputs.current[5]?.focus();
                      }
                    }} />
                ))}
              </div>
            </div>
          )}
          {step === 'request' && renderFields(requestFields, request, setRequest)}
          {step === 'details' && renderFields(detailFields, details, setDetails)}
          <div className="lp-registration-actions">
            <button type="submit" className="lp-submit" disabled={loading}>
              {step === 'verify' ? 'Verify Code' :
                step === 'request' ? 'Request Code' : 'Request Onboard'}
            </button>
            {step === 'request' && <button type="button" className="lp-submit" disabled={loading}
              onClick={() => {
                setStep('verify');
                setCode(Array(6).fill(''));
                setVerifiedRequest(null);
                setDetails({});
                setError('');
                setMessage('');
              }}>Verify Code</button>}
            <button type="button" className="lp-submit" disabled={loading} onClick={onBack}>Back to Login</button>
          </div>
        </form>
      )}
    </div>
  );
}
